"use client";

import { useEffect, useRef, useState } from "react";
import { usePublicComposition } from "./public-composition-context";
import { Share2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Explicit user copy; denied or stalled clipboard keeps a focused, selectable value. */
export function PublicCopyMenu({ label, actions }: { label: string; actions: Array<{ label: string; value: string | (() => string) }> }) {
  const { privatePreview } = usePublicComposition();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [manual, setManual] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const generation = useRef(0);
  useEffect(() => { if (manual) { input.current?.focus(); input.current?.select(); } }, [manual]);
  useEffect(() => () => { generation.current++; }, []);
  async function copy(value: string, action: string) {
    if (busy) return;
    const attempt = ++generation.current;
    setBusy(true); setCopied(null); setManual(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const ok = await Promise.race([
        navigator.clipboard?.writeText(value).then(() => true, () => false) ?? Promise.resolve(false),
        new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), 5000); }),
      ]);
      if (attempt !== generation.current) return;
      if (ok) setCopied(action); else setManual(value);
    } catch {
      if (attempt === generation.current) setManual(value);
    } finally {
      clearTimeout(timer);
      if (attempt === generation.current) setBusy(false);
    }
  }
  if (privatePreview) return null;
  return <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) { generation.current++; setBusy(false); setCopied(null); setManual(null); } }}>
    <PopoverTrigger asChild><button type="button" aria-label={label} className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"><Share2 className="h-4 w-4" /></button></PopoverTrigger>
    <PopoverContent align="end" className="max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-2">
      <p className="px-2 py-1 text-sm font-medium">{label}</p>
      {actions.map(action => <button key={action.label} type="button" disabled={busy} onClick={() => void copy(typeof action.value === "function" ? action.value() : action.value, action.label)} className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm hover:bg-muted disabled:opacity-50">{copied === action.label ? "Copied" : action.label}</button>)}
      {busy ? <p role="status" className="px-3 py-2 text-xs">Copying…</p> : null}
      {copied ? <p role="status" className="px-3 py-2 text-xs">Copied to clipboard.</p> : null}
      {manual ? <div className="space-y-2 p-2"><p role="status" className="text-sm">Copy is unavailable. Select and copy this value.</p><textarea ref={input} readOnly aria-label="Value to copy" value={manual} onFocus={event => event.currentTarget.select()} className="min-h-24 w-full rounded-md border bg-background p-2 text-xs" /></div> : null}
    </PopoverContent>
  </Popover>;
}
